import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useGetDefaultExpenseAccountQuery } from 'modules/bookkeep/settings/integrations/hooks/useGetDefaultExpenseAccountQuery';
import { TaskStepLayout } from 'modules/onboarding/components/TaskStepLayout';
import { useUpdateDefaultExpenseAccount } from 'modules/onboarding/setup-hub/pages/Accounting/AccountingExpenseAccounts/hooks/useUpdateDefaultExpenseAccount';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { routeFor } from 'src/core/constants/routes';

import { useUpdateCachedTaskStatus } from '../../SetupHubPage/hooks/useUpdateCachedTaskStatus';
import { routes } from '../../routes';
import type { DefaultExpenseAccountFormValues } from '../AccountingExpenseAccounts/hooks/form';
import { ExpenseAccountTableAndModals } from '../components/ExpenseAccountTableAndModals';

export const AccountingExpenseAccountsList = () => {
  const history = useHistory();
  const { t } = useTranslation('global');
  const companyId = useCompanyId();

  const updateCachedTaskStatus = useUpdateCachedTaskStatus();
  const defaultExpenseAccountQueryState = useGetDefaultExpenseAccountQuery();
  const defaultExpenseAccount = unwrapQuery(defaultExpenseAccountQueryState);

  const updateDefaultExpenseAccount = useUpdateDefaultExpenseAccount();

  const { values, errors, handleChange, handleSubmit, submitCount } =
    useFormik<DefaultExpenseAccountFormValues>({
      initialValues: {
        useDefaultValue:
          !defaultExpenseAccount?.isArchived && !!defaultExpenseAccount?.id,
        defaultExpenseAccountCode: defaultExpenseAccount?.code ?? '',
      },
      enableReinitialize: true,
      validate: (newValues) => {
        if (
          newValues.useDefaultValue &&
          !newValues.defaultExpenseAccountCode?.trim().length
        ) {
          return { defaultExpenseAccountCode: t('misc.requiredField') };
        }
      },
      onSubmit: async (newValues) => {
        await updateDefaultExpenseAccount(
          defaultExpenseAccount,
          newValues,
          onTaskCompleted,
        );
      },
    });

  const onBack = () => {
    const previousPath = routeFor(routes.ACCOUNTING_EXPENSE_ACCOUNTS.path, {
      company: companyId,
    });
    history.push(previousPath);
  };

  const onTaskCompleted = () => {
    updateCachedTaskStatus('expense_accounts_codes', 'setup_hub');
    const setupHubPath = routeFor(routes.SETUP_HUB_TASK_LIST.path, {
      company: companyId,
    });
    history.push(setupHubPath);
  };

  return (
    <>
      <div className="page__container">
        <TaskStepLayout
          description={t('setupHub.expenseAccounts.description')}
          extraDescription={
            <p className="text-content-secondary-bg-primary body-l">
              {t('setupHub.expenseAccounts.extraDescription')}
            </p>
          }
          isLastStep
          onBack={onBack}
          onNext={handleSubmit}
          title={t('setupHub.expenseAccounts.title')}
          video={{
            // TODO(GROW-1484): Replace with localized video URL
            url: '',
            title: t('setupHub.expenseAccounts.helpTitle'),
          }}
        >
          <p className="mb-16 title-m">
            {t('setupHub.expenseAccounts.listTitle')}
          </p>
          <ExpenseAccountTableAndModals
            values={values}
            handleChange={handleChange}
            errors={errors}
            submitCount={submitCount}
          />
        </TaskStepLayout>
      </div>
    </>
  );
};
